import React, { lazy } from 'react';
import { ExternalLinkIcon, BookOpenIcon, ArrowRightIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PageTransition } from '../components/layout/PageTransition';
import { DecorativeShape } from '../components/DecorativeShape';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Animate } from '../components/ui/Animate';
import { useLanguage } from '../context/LanguageContext';
const SUBSTACK_URL = 'https://helgig.substack.com';
interface Credential {
  code: string;
  label: string;
}
interface TimelineEvent {
  year: string;
  title: string;
  desc: string;
}
interface WritingItem {
  seriesLabel: string;
  title: string;
  description: string;
  tagColor: string;
  url: string;
  articleCount: string;
}
const credentials: Credential[] = [{
  code: 'Fil.Cand.',
  label: 'Work & Org. Psychology'
}, {
  code: 'ICF ACC',
  label: 'Certified Coach'
}, {
  code: 'ICP-LEA',
  label: 'Agile Leadership'
}, {
  code: 'ICP-AHR',
  label: 'Agility in HR'
}, {
  code: 'ICP-BAF',
  label: 'Business Agility'
}, {
  code: 'Clean Language',
  label: 'Systemic Modelling'
}];
const writingEN: WritingItem[] = [{
  seriesLabel: 'On Feedback',
  title: 'The Feedback Paradox',
  description: 'We build entire industries on the assumption that feedback is how people learn and develop. The research is more uncomfortable about this than the mainstream conversation admits. Five articles working through what feedback actually does, why it reveals more about the giver than the receiver, and what to do instead.',
  tagColor: 'bg-coral/10 text-coral',
  url: SUBSTACK_URL,
  articleCount: '5 articles'
}, {
  seriesLabel: 'On Agility',
  title: 'The Lessons of Agility',
  description: 'The Agile movement learned something important about how organisations can navigate complexity, respond to change, and do genuinely good work together. What those lessons actually are - stripped of the brand, the certifications, and the cargo-cult that grew up around them.',
  tagColor: 'bg-teal/10 text-teal',
  url: SUBSTACK_URL,
  articleCount: 'Prologue + 5 articles'
}, {
  seriesLabel: 'On Strategy',
  title: 'The Lessons of Strategy',
  description: 'Most organisations have a strategic plan. Very few have a strategy. The difference matters more than most people realise - and the gap between the two is where most strategic effort goes to die. Five articles on what strategy actually is, why coherence matters, and how to think about it in a changing landscape.',
  tagColor: 'bg-yellow-100 text-yellow-700',
  url: SUBSTACK_URL,
  articleCount: '5 articles'
}, {
  seriesLabel: 'On Teams',
  title: 'Developing Highly Effective Teams',
  description: 'Most people have more bad team experiences than good ones. That is not random - and it is not inevitable. What Wheelan, Tuckman and a body of research on team development actually show about how groups become high-functioning teams, and what most organisations get exactly backwards.',
  tagColor: 'bg-purple-100 text-purple-700',
  url: SUBSTACK_URL,
  articleCount: '4 articles'
}];
const writingIS: WritingItem[] = [{
  seriesLabel: 'Um endurgjöf',
  title: 'Endurgjafar þversögnin',
  description: 'Stór iðnaður hefur skapast kringum þá forsendu að endurgjöf sé lykillinn að þróun. Rannsóknir sýna óþægilegri mynd en við viljum gjarna viðurkenna. Fimm rannsóknarstuddar greinar sem fara m.a. í gegnum hvað endurgjöf gerir í raun, hvers vegna hún segir meira um gefandann en þiggjandann, og hvað við getum gert í staðinn sem virkar betur.',
  tagColor: 'bg-coral/10 text-coral',
  url: SUBSTACK_URL,
  articleCount: '5 greinar'
}, {
  seriesLabel: 'Um hröðun',
  title: 'Lærdómar Agile',
  description: 'Agile-hreyfingin lærði og færði okkur eitthvað mikilvægt um hvernig fyrirtæki geta siglt betur í gegnum flókinn veruleika og skapað öflug teymi sem ná betri og markvissari árangri í þróun. 5 greinar um lykil-lærdóma - óháð vörumerkinu "Agile", vottunum og "yfirborðs-agile" vinnubrögðum sem urðu til.',
  tagColor: 'bg-teal/10 text-teal',
  url: SUBSTACK_URL,
  articleCount: 'Formáli + 5 greinar'
}, {
  seriesLabel: 'Um stefnu',
  title: 'Lærdómur stefnumótunar',
  description: 'Flest fyrirtæki hafa stefnuáætlun. Mjög fá hafa stefnu. Munurinn skiptir meira máli en flestir gera sér grein fyrir. Fimm greinar um hvað stefna er í raun, hvers vegna samræmi skiptir máli, og hvernig við hugsum um það í umhverfi sem breytist.',
  tagColor: 'bg-yellow-100 text-yellow-700',
  url: SUBSTACK_URL,
  articleCount: '5 greinar'
}, {
  seriesLabel: 'Um teymi',
  title: 'Að þróa árangursrík teymi',
  description: 'Flestir hafa meiri slæma reynslu af teymisvinnu en góða. Það er ekki tilviljun - og það er ekki óumflýjanlegt. Hvað Wheelan, Tuckman og rannsóknar á teymisþróun sýna í raun um hvernig hópar verða áhrifarík teymi, og hvað flest fyrirtæki gera nákvæmlega öfugt.',
  tagColor: 'bg-purple-100 text-purple-700',
  url: SUBSTACK_URL,
  articleCount: '4 greinar'
}];
export function About() {
  const {
    t,
    language
  } = useLanguage();
  const timelineEvents = t('about.timeline') as TimelineEvent[];
  const publication = t('about.publication') as {
    title: string;
    subtitle: string;
    chapters: string;
  };
  const writing = language === 'is' ? writingIS : writingEN;
  return <PageTransition data-id="element-195">
      <div className="pt-12 pb-24 overflow-hidden" data-id="element-196">
        {/* Header */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20" data-id="element-197">
          <Animate className="text-center max-w-3xl mx-auto relative" data-id="element-198">
            <DecorativeShape type="circle" color="bg-yellow-200" className="top-0 left-1/2 w-32 h-32 -translate-x-1/2 -translate-y-1/2 -z-10 blur-xl opacity-50" data-id="element-199" />
            <h1 className="text-5xl md:text-6xl font-bold mb-6" data-id="element-200">
              {t('about.title')}
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed" data-id="element-201">
              {t('about.intro')}
            </p>
          </Animate>
        </section>

        {/* Story & Photo */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-24" data-id="element-202">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start" data-id="element-203">
            <div className="grid grid-cols-2 gap-4" data-id="element-204">
              <Animate className="space-y-4 mt-8" data-id="element-205">
                <img src="https://cdn.magicpatterns.com/uploads/6bt3PcPytwUVW4EyeZ9z5f/Helgi-6_A_height_x30_copy.jpg" alt="Helgi Gudmundsson" className="rounded-3xl shadow-lg w-full h-72 object-cover object-top" loading="lazy" decoding="async" data-id="element-206" />
                <div className="bg-coral p-6 rounded-3xl text-white flex items-center justify-center h-40" data-id="element-207">
                  <p className="font-heading font-bold text-xl text-center" data-id="element-208">
                    {t('about.pill1')}
                  </p>
                </div>
              </Animate>
              <Animate delay={0.2} className="space-y-4" data-id="element-209">
                <div className="bg-teal p-6 rounded-3xl text-white flex items-center justify-center h-40" data-id="element-210">
                  <p className="font-heading font-bold text-xl text-center" data-id="element-211">
                    {t('about.pill2')}
                  </p>
                </div>
                <img src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80" alt="Workshop facilitation" className="rounded-3xl shadow-lg w-full h-72 object-cover" loading="lazy" decoding="async" data-id="element-212" />
              </Animate>
            </div>

            <Animate delay={0.1} className="space-y-6 text-gray-600 text-lg leading-relaxed" data-id="element-213">
              <h2 className="text-3xl font-bold text-charcoal" data-id="element-214">
                {t('about.storyTitle')}
              </h2>
              <p data-id="element-215">{t('about.storyP1')}</p>
              <p data-id="element-216">{t('about.storyP2')}</p>
              <p data-id="element-217">{t('about.storyP3')}</p>
              <div className="border-l-4 border-teal pl-5 py-1 mt-6" data-id="element-218">
                <p className="text-sm font-bold text-teal uppercase tracking-wider mb-1" data-id="element-219">
                  {t('about.publicationLabel')}
                </p>
                <p className="font-bold text-charcoal text-base" data-id="element-220">
                  {publication.title}
                </p>
                <p className="text-sm text-gray-500" data-id="element-221">{publication.subtitle}</p>
                <p className="text-sm text-gray-500 italic mt-1" data-id="element-222">
                  {publication.chapters}
                </p>
              </div>
            </Animate>
          </div>
        </section>

        {/* Credentials */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-24" data-id="element-223">
          <Animate data-id="element-224">
            <h2 className="text-lg font-bold text-gray-400 uppercase tracking-wider text-center mb-8" data-id="element-225">
              {t('about.credentialsTitle')}
            </h2>
            <div className="flex flex-wrap justify-center gap-3" data-id="element-226">
              {credentials.map(c => <div key={c.code} className="flex flex-col items-center bg-white border border-gray-100 rounded-2xl px-5 py-3 shadow-sm" data-id="element-227">
                  <span className="font-bold text-charcoal text-sm" data-id="element-228">
                    {c.code}
                  </span>
                  <span className="text-gray-400 text-xs mt-0.5" data-id="element-229">
                    {c.label}
                  </span>
                </div>)}
            </div>
          </Animate>
        </section>

        {/* Timeline */}
        <section className="bg-cream py-24 relative" data-id="element-230">
          <DecorativeShape type="blob" color="bg-teal/10" className="bottom-0 right-0 w-96 h-96 translate-y-1/3 translate-x-1/3" data-id="element-231" />
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-232">
            <h2 className="text-3xl font-bold text-center mb-16" data-id="element-233">
              {t('about.timelineTitle')}
            </h2>
            <div className="relative" data-id="element-234">
              <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gray-200 rounded-full hidden md:block" data-id="element-235" />
              <div className="space-y-12" data-id="element-236">
                {timelineEvents.map((event, index) => <Animate key={index} direction={index % 2 === 0 ? 'right' : 'left'} delay={index * 0.1} className={`flex flex-col md:flex-row items-center gap-8 ${index % 2 === 0 ? 'md:flex-row-reverse' : ''}`} data-id="element-237">
                    <div className="flex-1 w-full md:w-auto" data-id="element-238">
                      <Card className="p-6 relative" data-id="element-239">
                        <span className="absolute -top-4 -right-4 bg-coral text-white font-bold px-3 py-1 rounded-full text-sm shadow-md" data-id="element-240">
                          {event.year}
                        </span>
                        <h3 className="text-xl font-bold mb-2 text-teal" data-id="element-241">
                          {event.title}
                        </h3>
                        <p className="text-gray-600" data-id="element-242">{event.desc}</p>
                      </Card>
                    </div>
                    <div className="relative z-10 flex-shrink-0" data-id="element-243">
                      <div className="w-4 h-4 bg-coral rounded-full ring-4 ring-white shadow-md hidden md:block" data-id="element-244" />
                    </div>
                    <div className="flex-1 hidden md:block" data-id="element-245" />
                  </Animate>)}
              </div>
            </div>
          </div>
        </section>

        {/* Writing */}
        <section className="py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-246">
          <Animate className="mb-12" data-id="element-247">
            <div className="flex items-center gap-3 mb-3" data-id="element-248">
              <BookOpenIcon size={20} className="text-teal" data-id="element-249" />
              <span className="text-teal font-bold tracking-wider uppercase text-sm" data-id="element-250">
                {t('about.writingLabel')}
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold" data-id="element-251">
              {t('about.writingTitle')}
            </h2>
            <p className="text-gray-500 mt-3 text-lg max-w-2xl" data-id="element-252">
              {t('about.writingSubtitle')}
            </p>
          </Animate>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10" data-id="element-253">
            {writing.map((item, i) => <Animate key={i} delay={i * 0.08} data-id="element-254">
                <a href={item.url} target="_blank" rel="noopener noreferrer" className="block h-full" data-id="element-255">
                  <Card hoverEffect className="h-full p-7 group border-2 border-transparent hover:border-teal/20 transition-colors flex flex-col" data-id="element-256">
                    <div className="flex items-start justify-between gap-4 mb-4" data-id="element-257">
                      <span className={`text-xs font-bold px-3 py-1 rounded-full ${item.tagColor}`} data-id="element-258">
                        {item.seriesLabel}
                      </span>
                      <ExternalLinkIcon size={16} className="text-gray-300 group-hover:text-teal transition-colors flex-shrink-0 mt-0.5" data-id="element-259" />
                    </div>
                    <h3 className="font-bold text-lg text-charcoal mb-3 group-hover:text-teal transition-colors leading-snug" data-id="element-260">
                      {item.title}
                    </h3>
                    <p className="text-gray-500 text-sm leading-relaxed flex-1" data-id="element-261">
                      {item.description}
                    </p>
                    <p className="text-xs text-gray-400 font-medium mt-4 pt-4 border-t border-gray-100" data-id="element-262">
                      {item.articleCount}
                    </p>
                  </Card>
                </a>
              </Animate>)}
          </div>

          <Animate delay={0.3} className="text-center" data-id="element-263">
            <a href={SUBSTACK_URL} target="_blank" rel="noopener noreferrer" data-id="element-264">
              <Button variant="outline" rightIcon={<ExternalLinkIcon size={16} data-id="element-266" />} data-id="element-265">
                {language === 'is' ? 'Sjá allar greinar á Substack' : 'View all writing on Substack'}
              </Button>
            </a>
          </Animate>
        </section>

        {/* Outside work */}
        <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 text-center" data-id="element-267">
          <Animate data-id="element-268">
            <p className="text-gray-400 text-lg leading-relaxed italic" data-id="element-269">
              {t('about.outsideWork')}
            </p>
          </Animate>
        </section>

        {/* CTA */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-8" data-id="element-270">
          <Animate data-id="element-271">
            <Card className="p-10 bg-gradient-to-br from-teal/5 to-coral/5 border-0 text-center" data-id="element-272">
              <h2 className="text-2xl font-bold mb-3" data-id="element-273">
                {language === 'is' ? 'Við skulum tala saman' : "Let's talk"}
              </h2>
              <p className="text-gray-500 mb-6 max-w-lg mx-auto" data-id="element-274">
                {language === 'is' ? 'Segðu mér frá stöðunni þinni og við finnum út hvað hentar best. Enginn þrýstingur - bara heiðarleg samtal.' : "Tell me about your situation and we'll find what fits. No pitch - just an honest conversation."}
              </p>
              <Link to="/contact" data-id="element-275">
                <Button variant="primary" rightIcon={<ArrowRightIcon size={18} data-id="element-277" />} data-id="element-276">
                  {language === 'is' ? 'Hafa samband' : 'Get in touch'}
                </Button>
              </Link>
            </Card>
          </Animate>
        </section>
      </div>
    </PageTransition>;
}